import { Minus, TrendingDown, TrendingUp } from 'lucide-react'
import type React from 'react'

interface VarianceChipProps {
    value: number
    previousValue?: number
    className?: string
    formatType?: 'percent' | 'lakh' | 'crore'
}

export const VarianceChip: React.FC<VarianceChipProps> = ({
    value,
    previousValue,
    className = '',
    formatType = 'percent',
}) => {
    // Trend Calculation
    // V4.1: Sign-based trend (Bias) to match user preference for performance metrics
    const isUp = value > 0
    const isDown = value < 0
    const isUnchanged = value === 0

    // Chip Styling
    const isPositive = value > 0

    // Exact StrikeChip Base Classes
    // relative w-24 h-8 flex items-center justify-center rounded-[10px] border text-[13px] font-bold tracking-tight shadow-sm transition-all mx-auto bg-white
    const baseClasses =
        'relative w-24 h-8 flex items-center justify-center rounded-[10px] border text-[13px] font-bold tracking-tight shadow-sm transition-all mx-auto bg-white dark:bg-slate-800'

    // StrikeChip Color Logic
    const colorClasses = isPositive
        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
        : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'

    const neutralClasses = 'border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-400'

    const formatDisplayValue = (val: number) => {
        if (formatType === 'percent') {
            return { num: Math.abs(val).toFixed(2), unit: '%' }
        }

        // Custom K/L/Cr formatting inline
        const abs = Math.abs(val)
        let numStr = ''
        let unitStr = ''
        if (abs >= 10000000) {
            numStr = (abs / 10000000).toFixed(2)
            unitStr = 'Cr'
        } else if (abs >= 100000) {
            numStr = (abs / 100000).toFixed(2)
            unitStr = 'L'
        } else if (abs >= 1000) {
            numStr = (abs / 1000).toFixed(2)
            unitStr = 'K'
        } else {
            numStr = abs.toFixed(0)
            unitStr = ''
        }
        return { num: numStr, unit: unitStr }
    }

    const { num, unit } = formatDisplayValue(value)

    return (
        <div className={`flex flex-col items-center justify-center ${className}`}>
            <div className={`${baseClasses} ${value !== 0 ? colorClasses : neutralClasses}`}>
                <span className="mr-1 flex items-baseline">
                    {value > 0 ? '+' : value < 0 ? '-' : ''}
                    {num}
                    <span className="text-[10px] ml-0.5">{unit}</span>
                </span>
                {isUp && <TrendingUp size={14} strokeWidth={2.5} />}
                {isDown && <TrendingDown size={14} strokeWidth={2.5} />}
                {isUnchanged && <Minus size={14} strokeWidth={2.5} className="opacity-50" />}
            </div>
        </div>
    )
}
